import { Stack, Typography } from '@mui/material';
import { MouseEvent, useState } from 'react';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { LinkBroken02Icon } from '@/components/icons/LinkBroken02Icon';
import { FlyoutMenuItem } from '@/components/poppers/MenuPopper/FlyoutMenuItem';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { StyledTableCell } from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { SidebarPerson } from '@/modules/familyTree/utils/relationshipUtils';
import { ClientProfileRelationshipType } from '@/types/schema';

const SPOUSE_OPTIONS = [
  { label: 'Spouse', value: ClientProfileRelationshipType.Spouse },
  { label: 'Partner', value: ClientProfileRelationshipType.DomesticPartner },
  { label: 'Ex-spouse', value: ClientProfileRelationshipType.ExSpouse },
  {
    label: 'Ex-partner',
    value: ClientProfileRelationshipType.ExDomesticPartner,
  },
];

export interface PersonRowProps {
  person: SidebarPerson;
  relationshipType:
    | 'Spouse'
    | 'Partner'
    | 'Ex-spouse'
    | 'Ex-partner'
    | 'Parent'
    | 'Stepparent'
    | 'Child'
    | 'Stepchild'
    | '';
  relationshipId: string;
  fromClientProfileId: string;
  toClientProfileId: string;
  onSelect?: (personId: string) => void;
  onUnlink?: (personId: string, relationshipId: string) => void;
  onChangeRelationshipType?: (
    relationshipId: string,
    fromClientProfileId: string,
    toClientProfileId: string,
    newType: ClientProfileRelationshipType
  ) => Promise<void>;
  isUnlinking?: boolean;
  isUpdatingRelationship?: boolean;
}

export function PersonRow({
  person,
  relationshipType,
  relationshipId,
  fromClientProfileId,
  toClientProfileId,
  onSelect,
  onUnlink,
  onChangeRelationshipType,
  isUnlinking = false,
  isUpdatingRelationship = false,
}: PersonRowProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const canChangeRelationship =
    !!onChangeRelationshipType &&
    SPOUSE_OPTIONS.some((option) => option.label === relationshipType);
  const isUpdating = isUnlinking || isUpdatingRelationship;

  const handleClick = () => {
    if (onSelect) {
      onSelect(person.id);
    }
  };

  return (
    <StyledTableRow
      onClick={onSelect ? handleClick : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={(event) => {
        if (
          event.target === event.currentTarget &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <StyledTableCell>
        <Stack>
          <Typography variant="title-xs" sx={{ overflowWrap: 'anywhere' }}>
            {person.displayName}
          </Typography>
          <Typography variant="body-xs">{relationshipType}</Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell
        align="right"
        onClick={(event: MouseEvent<HTMLTableCellElement>) =>
          event.stopPropagation()
        }
      >
        {(onUnlink || canChangeRelationship) && (
          <Stack alignItems="flex-end">
            <ContextMenuButton
              size="xs"
              variant="transparent"
              aria-label={`Actions for ${person.displayName}`}
              loading={isUnlinking}
              disabled={isUpdating}
              popperProps={{ disableEnforceFocus: true }}
              isOpen={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              onClose={() => setIsMenuOpen(false)}
            >
              {canChangeRelationship && (
                <FlyoutMenuItem
                  label="Change relationship"
                  disabled={isUpdating}
                  muiMenuItemProps={{
                    onClick: (event) => event.stopPropagation(),
                  }}
                >
                  {SPOUSE_OPTIONS.flatMap((option) => [
                    <MenuItem
                      key={option.value}
                      label={option.label}
                      iconAfter={
                        option.label === relationshipType ? (
                          <CheckIcon size={16} />
                        ) : undefined
                      }
                      disabled={isUpdating}
                      onClick={async () => {
                        setIsMenuOpen(false);
                        await onChangeRelationshipType?.(
                          relationshipId,
                          fromClientProfileId,
                          toClientProfileId,
                          option.value
                        );
                      }}
                      muiMenuItemProps={{
                        selected: option.label === relationshipType,
                      }}
                    />,
                    ...(option.value ===
                    ClientProfileRelationshipType.DomesticPartner
                      ? [<SelectItemGroupLabel key="ex-relationships" />]
                      : []),
                  ])}
                </FlyoutMenuItem>
              )}
              {canChangeRelationship && onUnlink && <SelectItemGroupLabel />}
              {onUnlink && (
                <MenuItem
                  label="Remove"
                  icon={<LinkBroken02Icon size={16} />}
                  disabled={isUpdating}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onUnlink(person.id, relationshipId);
                  }}
                />
              )}
            </ContextMenuButton>
          </Stack>
        )}
      </StyledTableCell>
    </StyledTableRow>
  );
}
